import React, { useEffect, useState, useRef, createElement } from 'react';
import { useForm } from 'react-hook-form';
import { SendIcon, LinkedinIcon, GithubIcon, FileTextIcon, PhoneIcon, MailIcon } from 'lucide-react';
import { contactData } from '../utils/constants';
import { ScheduleCallButton } from './ScheduleCallButton';
const Contact = ({
  language = 'en'
}) => {
  const {
    register,
    handleSubmit,
    formState: {
      errors
    },
    reset
  } = useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const elements = entry.target.querySelectorAll('.fade-in, .slide-in-left, .slide-in-right');
          elements.forEach((el, index) => {
            setTimeout(() => {
              el.classList.add('active');
            }, index * 100);
          });
        }
      });
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const onSubmit = data => {
    setIsSubmitting(true);
    setTimeout(() => {
      console.log(data);
      setIsSubmitting(false);
      setIsSubmitted(true);
      reset();
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    }, 1500);
  };
  const createRipple = event => {
    const button = event.currentTarget;
    const circle = document.createElement('span');
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;
    circle.style.width = circle.style.height = `${diameter}px`;
    circle.style.left = `${event.clientX - button.getBoundingClientRect().left - radius}px`;
    circle.style.top = `${event.clientY - button.getBoundingClientRect().top - radius}px`;
    circle.classList.add('ripple');
    const ripple = button.getElementsByClassName('ripple')[0];
    if (ripple) {
      ripple.remove();
    }
    button.appendChild(circle);
  };
  const translations = {
    en: {
      getInTouch: 'Get In',
      touchHighlight: 'Touch',
      name: 'Name',
      namePlaceholder: 'Your name',
      nameRequired: 'Name is required',
      email: 'Email',
      emailPlaceholder: 'Your email',
      emailRequired: 'Email is required',
      invalidEmail: 'Invalid email address',
      message: 'Message',
      messagePlaceholder: 'Your message',
      messageRequired: 'Message is required',
      sending: 'Sending...',
      send: 'Send Message',
      thankYou: 'Thank You!',
      messageSent: "Your message has been sent successfully. I'll get back to you soon!",
      contactInfo: 'Contact Information',
      phone: 'Phone',
      personalEmail: 'Personal Email',
      schoolEmail: 'School Email',
      connectWithMe: 'Connect with me',
      downloadResume: 'Download Resume',
      scheduleCall: 'Or schedule a call'
    },
    vi: {
      getInTouch: 'Liên',
      touchHighlight: 'Hệ',
      name: 'Họ Tên',
      namePlaceholder: 'Họ tên của bạn',
      nameRequired: 'Vui lòng nhập họ tên',
      email: 'Email',
      emailPlaceholder: 'Email của bạn',
      emailRequired: 'Vui lòng nhập email',
      invalidEmail: 'Email không hợp lệ',
      message: 'Tin Nhắn',
      messagePlaceholder: 'Tin nhắn của bạn',
      messageRequired: 'Vui lòng nhập tin nhắn',
      sending: 'Đang gửi...',
      send: 'Gửi Tin Nhắn',
      thankYou: 'Cảm Ơn!',
      messageSent: 'Tin nhắn của bạn đã được gửi thành công. Tôi sẽ phản hồi sớm!',
      contactInfo: 'Thông Tin Liên Hệ',
      phone: 'Điện Thoại',
      personalEmail: 'Email Cá Nhân',
      schoolEmail: 'Email Trường',
      connectWithMe: 'Kết nối với tôi',
      downloadResume: 'Tải CV',
      scheduleCall: 'Hoặc đặt lịch gọi'
    }
  };
  const t = translations[language];
  return <section ref={sectionRef} id="contact" className="w-full py-24 bg-gradient-to-br from-background via-secondary to-background relative overflow-hidden" data-id="element-294">
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-295">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse-slow" data-id="element-296" />
        <div className="absolute bottom-1/4 -right-48 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse-slow" data-id="element-297" />
      </div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-298">
        <div className="text-center mb-16" data-id="element-299">
          <h2 className="text-4xl md:text-5xl font-display font-bold mb-4 fade-in" data-id="element-300">
            {t.getInTouch}{' '}
            <span className="gradient-text" data-id="element-301">{t.touchHighlight}</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto" data-id="element-302">
          {/* Contact Form */}
          <div className="slide-in-left" data-id="element-303">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-id="element-304">
              <div data-id="element-305">
                <label htmlFor="name" className="block text-sm font-medium mb-2" data-id="element-306">
                  {t.name}
                </label>
                <input {...register('name', {
                required: t.nameRequired
              })} type="text" id="name" placeholder={t.namePlaceholder} className="w-full px-4 py-3 bg-secondary border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-accent-blue transition-all" data-id="element-307" />
                {errors.name && <p className="text-red-500 text-sm mt-1" data-id="element-308">
                    {errors.name.message}
                  </p>}
              </div>

              <div data-id="element-309">
                <label htmlFor="email" className="block text-sm font-medium mb-2" data-id="element-310">
                  {t.email}
                </label>
                <input {...register('email', {
                required: t.emailRequired,
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t.invalidEmail
                }
              })} type="email" id="email" placeholder={t.emailPlaceholder} className="w-full px-4 py-3 bg-secondary border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-accent-blue transition-all" data-id="element-311" />
                {errors.email && <p className="text-red-500 text-sm mt-1" data-id="element-312">
                    {errors.email.message}
                  </p>}
              </div>

              <div data-id="element-313">
                <label htmlFor="message" className="block text-sm font-medium mb-2" data-id="element-314">
                  {t.message}
                </label>
                <textarea {...register('message', {
                required: t.messageRequired
              })} id="message" rows={5} placeholder={t.messagePlaceholder} className="w-full px-4 py-3 bg-secondary border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-accent-blue transition-all resize-none" data-id="element-315" />
                {errors.message && <p className="text-red-500 text-sm mt-1" data-id="element-316">
                    {errors.message.message}
                  </p>}
              </div>

              {isSubmitted ? <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 text-center" data-id="element-317">
                  <h3 className="text-green-500 font-semibold mb-2" data-id="element-318">
                    {t.thankYou}
                  </h3>
                  <p className="text-sm text-muted" data-id="element-319">{t.messageSent}</p>
                </div> : <button type="submit" disabled={isSubmitting} onClick={createRipple} className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl font-semibold shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden flex items-center justify-center gap-2" data-id="element-320">
                  {isSubmitting ? <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" data-id="element-321"></div>
                      {t.sending}
                    </> : <>
                      <SendIcon size={20} data-id="element-322" />
                      {t.send}
                    </>}
                </button>}
            </form>
          </div>

          {/* Contact Info */}
          <div className="slide-in-right space-y-8" data-id="element-323">
            <div data-id="element-324">
              <h3 className="text-2xl font-bold mb-6" data-id="element-325">{t.contactInfo}</h3>
              <div className="space-y-4" data-id="element-326">
                <a href={`mailto:${contactData.emails.personal}`} className="flex items-center gap-4 p-4 bg-secondary/50 rounded-xl hover:bg-secondary transition-all group" data-id="element-327">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-328">
                    <MailIcon size={24} className="text-white" data-id="element-329" />
                  </div>
                  <div data-id="element-330">
                    <p className="text-sm text-muted" data-id="element-331">{t.personalEmail}</p>
                    <p className="font-medium" data-id="element-332">{contactData.emails.personal}</p>
                  </div>
                </a>

                <a href={`mailto:${contactData.emails.school}`} className="flex items-center gap-4 p-4 bg-secondary/50 rounded-xl hover:bg-secondary transition-all group" data-id="element-333">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-334">
                    <MailIcon size={24} className="text-white" data-id="element-335" />
                  </div>
                  <div data-id="element-336">
                    <p className="text-sm text-muted" data-id="element-337">{t.schoolEmail}</p>
                    <p className="font-medium" data-id="element-338">{contactData.emails.school}</p>
                  </div>
                </a>

                <a href={`tel:${contactData.phone}`} className="flex items-center gap-4 p-4 bg-secondary/50 rounded-xl hover:bg-secondary transition-all group" data-id="element-339">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-340">
                    <PhoneIcon size={24} className="text-white" data-id="element-341" />
                  </div>
                  <div data-id="element-342">
                    <p className="text-sm text-muted" data-id="element-343">{t.phone}</p>
                    <p className="font-medium" data-id="element-344">{contactData.phone}</p>
                  </div>
                </a>
              </div>
            </div>

            <div data-id="element-345">
              <h3 className="text-xl font-bold mb-4" data-id="element-346">{t.connectWithMe}</h3>
              <div className="flex gap-4" data-id="element-347">
                <a href={`https://${contactData.linkedin}`} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center hover:scale-110 transition-transform" data-id="element-348">
                  <LinkedinIcon size={24} className="text-white" data-id="element-349" />
                </a>
              </div>
            </div>

            <div className="pt-6" data-id="element-350">
              <p className="text-sm text-muted mb-4" data-id="element-351">{t.scheduleCall}</p>
              <ScheduleCallButton language={language} data-id="element-352" />
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default Contact;